import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AiCreateButton from '../../components/ai/AiCreateButton';
import { getInvoices, deleteInvoice, updateInvoice, voidInvoice, rectifyInvoice, getInvoicePdfUrl, exportInvoicesExcel, getInvoiceDuplicates, dedupeInvoices, createDeliveryNoteFromInvoice } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  FileText, Plus, Search, MoreVertical, Eye, Edit2, Trash2,
  Send, Check, Download, Upload, FileSpreadsheet, Ban, FileMinus,
  Wallet, Clock, AlertTriangle, Receipt, X as XIcon, Filter, Users,
  ArrowUpDown, Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import InvoiceDesignBanner from '../../components/invoices/InvoiceDesignBanner';
import PageHelpButton from '../../components/help/PageHelpButton';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    useGrouping: 'always', style: 'currency',
    currency: 'EUR'
  }).format(amount || 0);
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Borrador', color: 'bg-stone-100 text-stone-700' },
  { value: 'sent', label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
  { value: 'paid', label: 'Pagada', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'overdue', label: 'Vencida', color: 'bg-red-100 text-red-700' },
  { value: 'cancelled', label: 'Anulada', color: 'bg-stone-300 text-stone-700 line-through' },
];

const STATUS_CHIPS = [
  { value: 'all', label: 'Todas', dot: 'bg-stone-400' },
  { value: 'draft', label: 'Borrador', dot: 'bg-stone-400' },
  { value: 'sent', label: 'Enviadas', dot: 'bg-blue-500' },
  { value: 'paid', label: 'Pagadas', dot: 'bg-emerald-500' },
  { value: 'overdue', label: 'Vencidas', dot: 'bg-red-500' },
  { value: 'cancelled', label: 'Anuladas', dot: 'bg-stone-400' },
];

const QUARTERS = [
  { value: 'all', label: 'Todo el año' },
  { value: 'Q1', label: 'T1 · Ene-Mar' },
  { value: 'Q2', label: 'T2 · Abr-Jun' },
  { value: 'Q3', label: 'T3 · Jul-Sep' },
  { value: 'Q4', label: 'T4 · Oct-Dic' },
];

const statusLabels = STATUS_OPTIONS.reduce((acc, o) => { acc[o.value] = o.label; return acc; }, {});
const statusClass = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.color || 'bg-stone-100 text-stone-700';

const getYear = (iso) => {
  if (!iso) return null;
  const y = parseInt(iso.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
};
const getQuarter = (iso) => {
  if (!iso) return null;
  const m = parseInt(iso.slice(5, 7), 10);
  if (!Number.isFinite(m)) return null;
  if (m <= 3) return 'Q1';
  if (m <= 6) return 'Q2';
  if (m <= 9) return 'Q3';
  return 'Q4';
};

// Inline editable status select
const StatusInlineSelect = ({ invoice, onChange }) => {
  const isLocked = invoice.invoice_type === 'rectificativa' || invoice.status === 'cancelled';
  return (
    <Select
      value={invoice.status}
      onValueChange={(v) => onChange(invoice.id, v)}
      disabled={isLocked}
    >
      <SelectTrigger
        className={`h-7 px-2 py-0 text-xs font-semibold rounded-full border-0 ${statusClass(invoice.status)} ${isLocked ? 'opacity-70 cursor-not-allowed' : ''}`}
        data-testid={`status-select-${invoice.id}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} data-testid={`status-option-${opt.value}`}>
            <span className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${opt.color.split(' ')[0]}`}></span>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// Visual stat card
const StatCard = ({ label, value, sub, icon: Icon, gradient, iconBg, testid }) => (
  <div
    data-testid={testid}
    className={`relative overflow-hidden rounded-2xl p-5 border border-white/40 shadow-[0_8px_24px_rgb(0,0,0,0.06)] hover:shadow-[0_12px_30px_rgb(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-300 ${gradient}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone-500 mb-1">{label}</p>
        <p className="text-2xl font-extrabold text-stone-900 tracking-tight font-heading truncate">{value}</p>
        {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon className="w-5 h-5 text-white" strokeWidth={2} />
      </div>
    </div>
  </div>
);

// Filter chip (rectangle)
const FilterChip = ({ active, onClick, children, testid, dot }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`relative inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 whitespace-nowrap ${
      active
        ? 'bg-[#FF6B4A] text-white border-[#FF6B4A] shadow-md shadow-orange-200/60 -translate-y-0.5'
        : 'bg-white border-stone-200 text-stone-700 hover:border-orange-300 hover:bg-orange-50/40 shadow-sm'
    }`}
  >
    {dot && <span className={`inline-block w-1.5 h-1.5 rounded-full ${active ? 'bg-white/80' : dot}`} />}
    {children}
  </button>
);

export const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('invoices:sortBy') || 'issue_date_desc'; } catch { return 'issue_date_desc'; }
  });
  const [typeFilter, setTypeFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [quarterFilter, setQuarterFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [voidDialog, setVoidDialog] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [rectifyDialog, setRectifyDialog] = useState(null);
  const [rectifyReason, setRectifyReason] = useState('');
  const [rectifyMode, setRectifyMode] = useState('full');
  const [rectifyCode, setRectifyCode] = useState('R4');
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState(null);  // {total_groups, groups:[…]}
  const [dupesDialog, setDupesDialog] = useState(false);
  const [resolvingDupes, setResolvingDupes] = useState(false);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const res = await getInvoices({});
        setInvoices(res.data);
        setSelected(new Set());
      } catch (_e) {
        toast.error('Error al cargar facturas');
      } finally {
        setLoading(false);
      }
      try {
        const dRes = await getInvoiceDuplicates();
        setDupes(dRes.data);
      } catch (_e) {
        setDupes(null);
      }
    };
    fetchInitial();
  }, []);

  const loadInvoices = async () => {
    try {
      const res = await getInvoices({});
      setInvoices(res.data);
      setSelected(new Set());
    } catch (_e) {
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  const loadDuplicates = async () => {
    try {
      const res = await getInvoiceDuplicates();
      setDupes(res.data);
    } catch (_e) {
      setDupes(null);
    }
  };

  const handleResolveDupes = async () => {
    if (!window.confirm('Las facturas duplicadas más recientes se renombrarán con el sufijo -DUP-N. ¿Continuar?')) return;
    setResolvingDupes(true);
    try {
      const res = await dedupeInvoices();
      toast.success(`${res.data.invoices_renamed} factura(s) renombrada(s) en ${res.data.groups_resolved} grupo(s)`);
      setDupesDialog(false);
      await Promise.all([loadInvoices(), loadDuplicates()]);
    } catch (_e) {
      toast.error('Error al resolver duplicados');
    } finally {
      setResolvingDupes(false);
    }
  };

  const handleCreateDeliveryNote = async (invoice) => {
    if (!window.confirm(`¿Generar un albarán con los datos de ${invoice.invoice_number}?`)) return;
    try {
      const res = await createDeliveryNoteFromInvoice(invoice.id);
      toast.success(`Albarán ${res.data.delivery_number} creado`);
      await loadInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar albarán');
    }
  };

  // Unique years sorted desc
  const years = useMemo(() => {
    const set = new Set(invoices.map(i => getYear(i.issue_date)).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [invoices]);

  // Unique clients sorted alpha
  const clients = useMemo(() => {
    const set = new Set(invoices.map(i => i.client_name).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [invoices]);

  // Filtered + sorted list
  const filteredInvoices = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    const filtered = invoices.filter((inv) => {
      if (q) {
        const hay = `${inv.invoice_number || ''} ${inv.client_name || ''} ${inv.client_tax_id || ''} ${inv.notes || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
      if (typeFilter === 'rectificativa' && inv.invoice_type !== 'rectificativa') return false;
      if (typeFilter === 'normal' && inv.invoice_type === 'rectificativa') return false;
      if (yearFilter !== 'all' && getYear(inv.issue_date) !== parseInt(yearFilter, 10)) return false;
      if (quarterFilter !== 'all' && getQuarter(inv.issue_date) !== quarterFilter) return false;
      if (clientFilter !== 'all' && inv.client_name !== clientFilter) return false;
      return true;
    });

    // Apply client-side sort with secondary tiebreaker (invoice_number) so rows
    // sharing the same date/total don't shuffle randomly between renders.
    const cmpValue = (va, vb, dir) => {
      const aEmpty = va === null || va === undefined || va === '';
      const bEmpty = vb === null || vb === undefined || vb === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;   // empty values always go to the bottom
      if (bEmpty) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), 'es', { numeric: true });
    };
    const [field, direction] = sortBy.endsWith('_asc') ? [sortBy.replace('_asc', ''), 1] : [sortBy.replace('_desc', ''), -1];
    const sortKey = field === 'number' ? 'invoice_number' : field;
    return [...filtered].sort((a, b) => {
      const primary = cmpValue(a?.[sortKey], b?.[sortKey], direction);
      if (primary !== 0) return primary;
      // Tiebreaker 1: invoice_number desc (newest correlative first)
      if (sortKey !== 'invoice_number') {
        const t1 = cmpValue(a?.invoice_number, b?.invoice_number, -1);
        if (t1 !== 0) return t1;
      }
      // Tiebreaker 2: created_at desc
      return cmpValue(a?.created_at, b?.created_at, -1);
    });
  }, [invoices, searchTerm, statusFilter, typeFilter, yearFilter, quarterFilter, clientFilter, sortBy]);

  // Stats on filtered set (so they react to filters)
  const stats = useMemo(() => {
    const acc = { facturado: 0, cobrado: 0, pendiente: 0, vencidas: 0, count: filteredInvoices.length, vencidasCount: 0 };
    filteredInvoices.forEach((inv) => {
      if (inv.status === 'cancelled') return;
      const total = Number(inv.total) || 0;
      acc.facturado += total;
      if (inv.status === 'paid') acc.cobrado += total;
      else if (inv.status === 'overdue') { acc.vencidas += total; acc.pendiente += total; acc.vencidasCount += 1; }
      else if (inv.status !== 'draft') acc.pendiente += total;
    });
    return acc;
  }, [filteredInvoices]);

  const allSelected = filteredInvoices.length > 0 && filteredInvoices.every(inv => selected.has(inv.id));

  const activeFiltersCount = [
    statusFilter !== 'all',
    typeFilter !== 'all',
    yearFilter !== 'all',
    quarterFilter !== 'all',
    clientFilter !== 'all',
    searchTerm.trim() !== '',
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setTypeFilter('all');
    setYearFilter('all'); setQuarterFilter('all'); setClientFilter('all');
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filteredInvoices.map(inv => inv.id)));
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta factura?')) return;
    try {
      await deleteInvoice(id);
      toast.success('Factura eliminada');
      loadInvoices();
    } catch {
      toast.error('Error al eliminar factura');
    }
  };

  const handleBulkDelete = async () => {
    const count = selected.size;
    if (!window.confirm(`¿Eliminar ${count} factura${count > 1 ? 's' : ''} seleccionada${count > 1 ? 's' : ''}?`)) return;
    setDeleting(true);
    let deleted = 0;
    let errors = 0;
    for (const id of selected) {
      try { await deleteInvoice(id); deleted++; } catch { errors++; }
    }
    setDeleting(false);
    if (deleted > 0) toast.success(`${deleted} factura${deleted > 1 ? 's' : ''} eliminada${deleted > 1 ? 's' : ''}`);
    if (errors > 0) toast.error(`${errors} factura${errors > 1 ? 's' : ''} no se pudieron eliminar`);
    loadInvoices();
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateInvoice(id, { status: newStatus });
      toast.success(`Estado: ${statusLabels[newStatus] || newStatus}`);
      loadInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  const handleVoid = async () => {
    if (!voidDialog) return;
    setBusy(true);
    try {
      await voidInvoice(voidDialog.invoice.id, voidReason || null);
      toast.success('Factura anulada');
      setVoidDialog(null);
      setVoidReason('');
      loadInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo anular');
    } finally {
      setBusy(false);
    }
  };

  const handleRectify = async () => {
    if (!rectifyDialog) return;
    if (!rectifyReason.trim()) { toast.error('Indica el motivo de rectificación'); return; }
    if (!rectifyCode) { toast.error('Selecciona la causa AEAT'); return; }
    setBusy(true);
    try {
      const { data } = await rectifyInvoice(rectifyDialog.invoice.id, {
        reason: rectifyReason.trim(),
        rectification_code: rectifyCode,
        mode: rectifyMode,
      });
      toast.success(`Rectificativa ${data.invoice_number} creada como borrador`);
      setRectifyDialog(null);
      setRectifyReason(''); setRectifyMode('full'); setRectifyCode('R4');
      loadInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear la rectificativa');
    } finally {
      setBusy(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Exportando facturas...');
      await exportInvoicesExcel();
      toast.success('Facturas exportadas');
    } catch {
      toast.error('Error al exportar facturas');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Facturas</h1>
            <p className="text-stone-500 text-sm mt-1">Gestiona todas tus facturas</p>
          </div>
          <PageHelpButton moduleKey="invoices" variant="icon" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <Button variant="outline" className="rounded-xl" onClick={handleExportExcel} data-testid="export-invoices-excel-button">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Link to="/import?entity=invoices" className="w-full sm:w-auto">
            <Button variant="outline" className="rounded-xl w-full sm:w-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="import-invoices-button">
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
          </Link>
          <Link to="/invoices/new" className="w-full sm:w-auto">
            <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto" data-testid="create-invoice-button">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Factura
            </Button>
          </Link>
          <AiCreateButton entity="invoice" className="w-full sm:w-auto" />
        </div>
      </div>

      {/* Duplicate-invoice warning banner */}
      {dupes && dupes.total_groups > 0 && (
        <div
          className="rounded-2xl bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-200 px-4 py-3 flex items-center gap-3"
          data-testid="dupes-banner"
        >
          <div className="w-9 h-9 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-rose-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-rose-900">
              {dupes.total_groups} {dupes.total_groups === 1 ? 'factura duplicada' : 'facturas duplicadas'} detectada{dupes.total_groups === 1 ? '' : 's'}
            </div>
            <div className="text-xs text-rose-700/80">
              Verifactu exige número único por serie. Revísalas y resuelve los duplicados.
            </div>
          </div>
          <Button
            size="sm"
            className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl"
            onClick={() => setDupesDialog(true)}
            data-testid="open-dupes-dialog"
          >
            Resolver
          </Button>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="invoice-stats">
        <StatCard
          label="Facturado"
          value={formatCurrency(stats.facturado)}
          sub={`${stats.count} factura${stats.count === 1 ? '' : 's'}`}
          icon={Receipt}
          gradient="bg-gradient-to-br from-orange-50 to-amber-50"
          iconBg="bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24]"
          testid="stat-facturado"
        />
        <StatCard
          label="Cobrado"
          value={formatCurrency(stats.cobrado)}
          sub={stats.facturado > 0 ? `${Math.round((stats.cobrado / stats.facturado) * 100)}% del total` : '—'}
          icon={Wallet}
          gradient="bg-gradient-to-br from-emerald-50 to-teal-50"
          iconBg="bg-gradient-to-br from-emerald-500 to-teal-500"
          testid="stat-cobrado"
        />
        <StatCard
          label="Pendiente"
          value={formatCurrency(stats.pendiente)}
          sub="Enviadas + vencidas"
          icon={Clock}
          gradient="bg-gradient-to-br from-sky-50 to-blue-50"
          iconBg="bg-gradient-to-br from-sky-500 to-blue-500"
          testid="stat-pendiente"
        />
        <StatCard
          label="Vencidas"
          value={formatCurrency(stats.vencidas)}
          sub={`${stats.vencidasCount} factura${stats.vencidasCount === 1 ? '' : 's'}`}
          icon={AlertTriangle}
          gradient="bg-gradient-to-br from-rose-50 to-red-50"
          iconBg="bg-gradient-to-br from-rose-500 to-red-500"
          testid="stat-vencidas"
        />
      </div>

      {/* Invoice Design Banner */}
      <InvoiceDesignBanner />

      {/* Filter Card */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5 space-y-4" data-testid="filters-card">
        {/* Top row: search + client + reset */}
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <Input
              placeholder="Buscar por número, cliente, NIF o notas…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white"
              data-testid="search-invoices-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-stone-600 transition-colors"
                data-testid="clear-search"
              >
                <XIcon className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="relative lg:w-64">
            <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="client-filter-select">
                <SelectValue placeholder="Todos los clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los clientes</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`client-option-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative lg:w-56">
            <ArrowUpDown className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
            <Select
              value={sortBy}
              onValueChange={(v) => {
                setSortBy(v);
                try { localStorage.setItem('invoices:sortBy', v); } catch (_e) { /* ignore */ }
              }}
            >
              <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="issue_date_desc">Más recientes primero</SelectItem>
                <SelectItem value="issue_date_asc">Más antiguas primero</SelectItem>
                <SelectItem value="number_desc">Nº factura (mayor→menor)</SelectItem>
                <SelectItem value="number_asc">Nº factura (menor→mayor)</SelectItem>
                <SelectItem value="total_desc">Importe (mayor→menor)</SelectItem>
                <SelectItem value="total_asc">Importe (menor→mayor)</SelectItem>
                <SelectItem value="client_name_asc">Cliente (A → Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {activeFiltersCount > 0 && (
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl text-sm font-semibold text-stone-600 bg-stone-50 hover:bg-stone-100 border border-stone-200 transition-all whitespace-nowrap"
              data-testid="reset-filters"
            >
              <XIcon className="w-3.5 h-3.5" /> Limpiar ({activeFiltersCount})
            </button>
          )}
        </div>

        {/* Year row */}
        {years.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap pt-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400 flex items-center gap-1.5">
              <Filter className="w-3 h-3" /> Año
            </span>
            <div className="flex gap-2 flex-wrap" data-testid="year-filters">
              <FilterChip active={yearFilter === 'all'} onClick={() => setYearFilter('all')} testid="year-chip-all">Todos</FilterChip>
              {years.map((y) => (
                <FilterChip key={y} active={yearFilter === String(y)} onClick={() => setYearFilter(String(y))} testid={`year-chip-${y}`}>
                  {y}
                </FilterChip>
              ))}
            </div>
          </div>
        )}

        {/* Quarter row */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400 flex items-center gap-1.5">
            <Filter className="w-3 h-3" /> Trimestre
          </span>
          <div className="flex gap-2 flex-wrap" data-testid="quarter-filters">
            {QUARTERS.map((q) => (
              <FilterChip key={q.value} active={quarterFilter === q.value} onClick={() => setQuarterFilter(q.value)} testid={`quarter-chip-${q.value}`}>
                {q.label}
              </FilterChip>
            ))}
          </div>
        </div>

        {/* Status row */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400 flex items-center gap-1.5">
            <Filter className="w-3 h-3" /> Estado
          </span>
          <div className="flex gap-2 flex-wrap" data-testid="status-filters">
            {STATUS_CHIPS.map((s) => (
              <FilterChip key={s.value} active={statusFilter === s.value} onClick={() => setStatusFilter(s.value)} dot={s.dot} testid={`status-chip-${s.value}`}>
                {s.label}
              </FilterChip>
            ))}
          </div>
        </div>

        {/* Type row */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400 flex items-center gap-1.5">
            <Filter className="w-3 h-3" /> Tipo
          </span>
          <div className="flex gap-2 flex-wrap" data-testid="type-filters">
            <FilterChip active={typeFilter === 'all'} onClick={() => setTypeFilter('all')} testid="type-chip-all">Todas</FilterChip>
            <FilterChip active={typeFilter === 'normal'} onClick={() => setTypeFilter('normal')} testid="type-chip-normal">Normales</FilterChip>
            <FilterChip active={typeFilter === 'rectificativa'} onClick={() => setTypeFilter('rectificativa')} testid="type-chip-rectificativa">Rectificativas</FilterChip>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-xl px-4 py-2.5" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium text-stone-700">
            {selected.size} seleccionada{selected.size > 1 ? 's' : ''}
          </span>
          <Button variant="destructive" size="sm" className="rounded-xl" onClick={handleBulkDelete} disabled={deleting} data-testid="bulk-delete-button">
            <Trash2 className="w-4 h-4 mr-1.5" />
            {deleting ? 'Eliminando...' : `Eliminar (${selected.size})`}
          </Button>
          <Button variant="ghost" size="sm" className="rounded-xl text-stone-500" onClick={() => setSelected(new Set())} data-testid="bulk-deselect-button">
            Deseleccionar
          </Button>
        </div>
      )}

      {/* Invoices Table */}
      {filteredInvoices.length > 0 ? (
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-hidden" data-testid="invoices-table-card">
          <div className="overflow-x-auto">
            <table className="w-full invoice-table">
              <thead>
                <tr>
                  <th className="w-10 px-4 py-3">
                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} aria-label="Seleccionar todas" data-testid="select-all-checkbox" />
                  </th>
                  <th className="text-left px-4 py-3">Número</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Fecha</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Vencimiento</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className={`hover:bg-orange-50 transition-colors ${selected.has(invoice.id) ? 'bg-[#FF6B4A]/5' : ''}`}
                    data-testid={`invoice-row-${invoice.id}`}
                  >
                    <td className="w-10 px-4 py-4">
                      <Checkbox
                        checked={selected.has(invoice.id)}
                        onCheckedChange={() => toggleSelect(invoice.id)}
                        aria-label={`Seleccionar factura ${invoice.invoice_number}`}
                        data-testid={`select-invoice-${invoice.id}`}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <Link to={`/invoices/${invoice.id}`} className="font-mono text-sm text-[#FF6B4A] hover:underline">
                        {invoice.invoice_number || <span className="text-stone-400 italic">BORRADOR</span>}
                      </Link>
                    </td>
                    <td className="px-4 py-4 text-sm">{invoice.client_name}</td>
                    <td className="px-4 py-4 text-sm text-stone-500 hidden md:table-cell">{invoice.issue_date}</td>
                    <td className="px-4 py-4 text-sm text-stone-500 hidden md:table-cell">{invoice.due_date}</td>
                    <td className="px-4 py-4">
                      <StatusInlineSelect invoice={invoice} onChange={handleStatusChange} />
                      {invoice.invoice_type === 'rectificativa' && (
                        <span className="ml-1 text-[9px] font-bold text-purple-600 uppercase tracking-wide">Rect.</span>
                      )}
                      {invoice.rectified_by_invoice_number && (
                        <div className="text-[9px] text-stone-500 mt-0.5">↻ {invoice.rectified_by_invoice_number}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right font-mono text-sm font-medium">
                      {formatCurrency(invoice.total)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 hover:bg-stone-100 rounded-xl" data-testid={`invoice-menu-${invoice.id}`}>
                            <MoreVertical className="w-4 h-4 text-stone-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/invoices/${invoice.id}`} className="flex items-center gap-2">
                              <Eye className="w-4 h-4" /> Ver
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/invoices/${invoice.id}/edit`} className="flex items-center gap-2">
                              <Edit2 className="w-4 h-4" /> Editar
                            </Link>
                          </DropdownMenuItem>
                          {invoice.status === 'draft' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'sent')}>
                              <Send className="w-4 h-4 mr-2" /> Marcar como Enviada
                            </DropdownMenuItem>
                          )}
                          {invoice.status === 'sent' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'paid')}>
                              <Check className="w-4 h-4 mr-2" /> Marcar como Pagada
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => window.open(getInvoicePdfUrl(invoice.id), '_blank')}>
                            <Download className="w-4 h-4 mr-2" /> Descargar PDF
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {invoice.status !== 'cancelled' && invoice.invoice_type !== 'rectificativa' && (
                            <DropdownMenuItem onClick={() => setRectifyDialog({ invoice })} className="text-purple-700 focus:text-purple-700" data-testid={`rectify-${invoice.id}`}>
                              <FileMinus className="w-4 h-4 mr-2" /> Crear rectificativa
                            </DropdownMenuItem>
                          )}
                          {invoice.status !== 'cancelled' && !invoice.linked_delivery_note_id && (
                            <DropdownMenuItem
                              onClick={() => handleCreateDeliveryNote(invoice)}
                              className="text-sky-700 focus:text-sky-700"
                              data-testid={`make-delivery-note-${invoice.id}`}
                            >
                              <Truck className="w-4 h-4 mr-2" /> Generar albarán
                            </DropdownMenuItem>
                          )}
                          {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                            <DropdownMenuItem onClick={() => setVoidDialog({ invoice })} className="text-amber-700 focus:text-amber-700" data-testid={`void-${invoice.id}`}>
                              <Ban className="w-4 h-4 mr-2" /> Anular factura
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDelete(invoice.id)} className="text-red-600 focus:text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100" data-testid="empty-state">
          <FileText className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-medium text-stone-900 mb-1">
            {activeFiltersCount > 0 ? 'Sin resultados para los filtros aplicados' : 'No hay facturas'}
          </h3>
          <p className="text-stone-500 mb-4">
            {activeFiltersCount > 0 ? 'Prueba a limpiar algún filtro o ajustar la búsqueda.' : 'Crea tu primera factura para empezar'}
          </p>
          {activeFiltersCount > 0 ? (
            <button onClick={resetFilters} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold text-sm transition-all" data-testid="empty-reset-filters">
              <XIcon className="w-4 h-4" /> Limpiar filtros
            </button>
          ) : (
            <Link to="/invoices/new">
              <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="create-first-invoice-button">
                <Plus className="w-4 h-4 mr-2" /> Crear Factura
              </Button>
            </Link>
          )}
        </div>
      )}

      {/* Anular factura dialog */}
      <Dialog open={!!voidDialog} onOpenChange={(o) => !o && setVoidDialog(null)}>
        <DialogContent data-testid="void-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-amber-600" /> Anular factura {voidDialog?.invoice?.invoice_number}</DialogTitle>
            <DialogDescription>
              La factura quedará marcada como <strong>anulada</strong> y dejará de contar en los cómputos fiscales. El registro se conserva para trazabilidad. <br />
              <span className="text-amber-700">¿Ya cobraste o enviaste esta factura?</span> Lo correcto es emitir una <strong>rectificativa</strong> en su lugar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="void-reason">Motivo (opcional pero recomendado)</Label>
            <Textarea id="void-reason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="Ej: factura emitida por error, cliente equivocado, importe duplicado…" className="rounded-xl" rows={3} data-testid="void-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVoidDialog(null)}>Cancelar</Button>
            <Button onClick={handleVoid} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="void-confirm-btn">
              {busy ? 'Anulando…' : 'Anular factura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crear rectificativa dialog */}
      <Dialog open={!!rectifyDialog} onOpenChange={(o) => !o && setRectifyDialog(null)}>
        <DialogContent data-testid="rectify-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileMinus className="w-5 h-5 text-purple-700" /> Rectificar factura {rectifyDialog?.invoice?.invoice_number}</DialogTitle>
            <DialogDescription>
              Crearemos una nueva factura tipo <strong>rectificativa</strong> (prefijo REC-) en estado borrador. La original queda referenciada y puedes editarla antes de enviar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Causa AEAT (RD 1619/2012) *</Label>
              <Select value={rectifyCode} onValueChange={setRectifyCode}>
                <SelectTrigger data-testid="rectify-code-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="R1">R1 — Errores fundados en derecho (Art. 80.1/2/6 LIVA)</SelectItem>
                  <SelectItem value="R2">R2 — Concurso de acreedores (Art. 80.3 LIVA)</SelectItem>
                  <SelectItem value="R3">R3 — Créditos incobrables (Art. 80.4 LIVA)</SelectItem>
                  <SelectItem value="R4">R4 — Otras causas (general)</SelectItem>
                  <SelectItem value="R5">R5 — Facturas simplificadas rectificadas</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-stone-500">Código fiscal obligatorio que se incluye en las notas y servirá para el libro de facturas emitidas.</p>
            </div>
            <div className="space-y-2">
              <Label>Tipo de rectificación</Label>
              <Select value={rectifyMode} onValueChange={setRectifyMode}>
                <SelectTrigger data-testid="rectify-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Anular completa (importes en negativo)</SelectItem>
                  <SelectItem value="partial">Parcial (ajusto importes después)</SelectItem>
                  <SelectItem value="replace">Sustitutiva (datos correctos)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500">
                {rectifyMode === 'full' && 'Genera líneas con cantidad negativa para anular el 100%.'}
                {rectifyMode === 'partial' && 'Se crea como borrador vacío para que añadas la diferencia.'}
                {rectifyMode === 'replace' && 'Se crea como borrador vacío para que introduzcas los datos correctos.'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rectify-reason">Motivo de rectificación *</Label>
              <Textarea id="rectify-reason" value={rectifyReason} onChange={(e) => setRectifyReason(e.target.value)} placeholder="Ej: error en el importe, datos fiscales incorrectos del cliente, descuento no aplicado…" className="rounded-xl" rows={3} data-testid="rectify-reason-input" required />
              <p className="text-[10px] text-stone-500">Aparecerá en las notas de la factura rectificativa (requisito AEAT).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRectifyDialog(null)}>Cancelar</Button>
            <Button onClick={handleRectify} disabled={busy || !rectifyReason.trim()} className="bg-purple-700 hover:bg-purple-800 text-white" data-testid="rectify-confirm-btn">
              {busy ? 'Creando…' : 'Crear rectificativa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicates resolution modal */}
      <Dialog open={dupesDialog} onOpenChange={setDupesDialog}>
        <DialogContent className="max-w-2xl" data-testid="dupes-dialog">
          <DialogHeader>
            <DialogTitle>Facturas con número duplicado</DialogTitle>
            <DialogDescription>
              Verifactu exige unicidad de número por serie. Las facturas más antiguas se conservan
              intactas; las más recientes se renombrarán a <span className="font-mono">N-DUP-1</span>,
              <span className="font-mono">N-DUP-2</span>… para que puedas revisarlas y, si procede,
              anularlas o emitir rectificativas.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto -mx-2 px-2 space-y-3">
            {dupes?.groups?.map((g) => (
              <div key={g.invoice_number} className="rounded-xl border border-stone-200 p-3 bg-stone-50/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono font-bold text-stone-900">{g.invoice_number}</div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-bold">×{g.count}</span>
                </div>
                <div className="space-y-1.5 text-xs">
                  {g.invoices.map((inv, idx) => (
                    <div key={inv.id} className="flex items-center gap-2 text-stone-600">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${idx === 0 ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                      <span className="font-medium text-stone-900 truncate flex-1">{inv.client_name || '—'}</span>
                      <span className="font-mono">{inv.issue_date || '—'}</span>
                      <span className="font-mono font-bold text-stone-900">{formatCurrency(inv.total || 0)}</span>
                      <span className="px-1.5 py-0.5 rounded bg-white border border-stone-200 text-[10px] uppercase">{inv.status}</span>
                      {idx === 0 ? (
                        <span className="text-emerald-700 font-bold text-[10px]">SE MANTIENE</span>
                      ) : (
                        <span className="text-rose-700 font-bold text-[10px]">→ -DUP-{idx}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDupesDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleResolveDupes}
              disabled={resolvingDupes || !dupes?.total_groups}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="resolve-dupes-btn"
            >
              {resolvingDupes ? 'Resolviendo…' : `Resolver ${dupes?.total_groups || 0} grupo(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
